'use client';
import { Button, Input, message, Spin, Upload, UploadFile } from 'antd';
import styles from './customInput.module.scss';
import { AttachmentType, ChatType, MessageType, ThemeEnum } from 'types';
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from 'context';
import { COLORS } from 'utilities';
import { IoMicOutline } from 'react-icons/io5';
import { ExtendedUploadRef } from './customInput.type';
import { CgAttachment } from 'react-icons/cg';
import { BiSend } from 'react-icons/bi';
import { MdOutlinePauseCircle } from 'react-icons/md';
import { IoMdTrash } from 'react-icons/io';
import { RestAPI, useRealTimeState } from 'scripts';
import { API } from 'data';

function CustomInputComp(props: { chat?: ChatType; onSubmit: (e: MessageType) => void }) {
    const { theme, chatDeactivateMessage } = useContext(GlobalContext);
    const uploadRef = useRef<ExtendedUploadRef>(null);
    const fileLists = useRealTimeState<Array<UploadFile>>([]);
    const files = useRealTimeState<AttachmentType[]>([]);
    const [messageValue, setMessageValue] = useState('');
    const [inputStatus, setInputStatus] = useState<'voice' | 'text'>('text');
    // Voice Recorder States
    const [recordStatus, setRecordStatus] = useState<'recording' | 'stop' | 'permission'>('permission');
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const [count, setCount] = useState(0);
    const [isRunning, setIsRunning] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleButtonClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        uploadRef.current?.upload?.uploader?.onClick(e);
    };

    function submitMessage() {
        if (chatDeactivateMessage) return;
        setLoading(true);
        RestAPI.post<MessageType>(API.chat.sendMessage, {
            file: files.get().map((i) => i.id),
            status: 1,
            chat: props.chat?.id,
            text: messageValue,
        })
            .then((res) => {
                props.onSubmit(res.data);
                setLoading(false);
                setMessageValue('');
                fileLists.set([]);
                files.set([]);
            })
            .catch(() => {
                message.error('خطایی رخ داده است. مجددا تلاش نمایید');
                setLoading(false);
            });
    }

    function voiceSubmit() {
        if (!audioBlob || chatDeactivateMessage) return;
        setLoading(true);
        const body = new FormData();
        const FILE = new File([audioBlob], 'voice.mp3');
        body.append('file', FILE);
        body.append('alt', FILE.name);
        RestAPI.form<AttachmentType>(API.core.attachment.index, body)
            .then((res) => {
                RestAPI.post<MessageType>(API.chat.sendMessage, {
                    file: [res.data.id],
                    status: 1,
                    chat: props.chat?.id,
                    text: '____ALOEJARE_VOICE_MESSAGE____',
                })
                    .then((res) => {
                        props.onSubmit(res.data);
                        setLoading(false);
                        setMessageValue('');
                        fileLists.set([]);
                        files.set([]);
                        removeVoiceRecorder();
                    })
                    .catch(() => {
                        message.error('خطایی رخ داده است. مجددا تلاش نمایید');
                        setLoading(false);
                    });
            })
            .catch(() => {
                message.error('خطایی رخ داده است. مجددا تلاش نمایید');
                setLoading(false);
            });
    }

    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (isRunning) {
            interval = setInterval(() => {
                setCount((prevCount) => prevCount + 1);
            }, 1000);
        }

        return () => clearInterval(interval);
    }, [isRunning, count]);
    const startTimer = () => {
        setIsRunning(true);
    };
    const stopTimer = () => {
        setIsRunning(false);
    };
    const formatTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secondsLeft = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secondsLeft.toString().padStart(2, '0')}`;
    };
    const openVoiceRecorder = () => {
        setAudioBlob(null);
        permissionRecorder();
    };
    const startVoiceRecorder = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.start();
            setRecordStatus('recording');
            startTimer();
        }
    };
    const permissionRecorder = async () => {
        navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then((stream) => {
                mediaRecorderRef.current = new MediaRecorder(stream);
                mediaRecorderRef.current.ondataavailable = (event) => {
                    setAudioBlob(event.data);
                };
                setInputStatus('voice');
                setRecordStatus('recording');
                startVoiceRecorder();
            })
            .catch((error) => {
                setInputStatus('text');
                console.error('Error accessing microphone:', error);
            });
    };
    const stopVoiceRecorder = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            setRecordStatus('stop');
            stopTimer();
        }
    };

    const removeVoiceRecorder = () => {
        if (mediaRecorderRef.current) {
            const stream = mediaRecorderRef.current.stream;
            const tracks = stream.getTracks();
            tracks.forEach((track) => track.stop());
        }
        stopVoiceRecorder();
        setAudioBlob(null);
        setCount(0);
        setInputStatus('text');
        setRecordStatus('permission');
    };

    const onUpload = (f: File) => {
        const list = [...fileLists.get()];
        const uid = `-${list.length + 1}`;
        list.push({
            uid,
            percent: 0,
            name: f.name,
            status: 'uploading',
        });
        fileLists.set(list);
        const body = new FormData();
        body.append('file', f);
        body.append('alt', f.name);
        RestAPI.form<AttachmentType>(API.core.attachment.index, body)
            .then((res) => {
                const newList = fileLists.get();
                const index = newList.findIndex((i) => i.uid === uid);
                if (index >= 0)
                    newList[index] = {
                        uid: res.data.id.toString(),
                        percent: 100,
                        name: f.name,
                        status: 'done',
                        url: res.data.file,
                    };
                files.set([...files.get(), res.data]);
                fileLists.set(newList);
            })
            .catch(() => {
                const newList = [...fileLists.get()];
                const index = newList.findIndex((i) => i.uid === uid);
                if (index >= 0)
                    newList[index] = {
                        uid: f.name,
                        name: f.name,
                        status: 'error',
                    };
                fileLists.set(newList);
            });
    };

    return (
        <div className={`${styles['input-layout']}`}>
            {inputStatus === 'text' ? (
                <>
                    <Upload
                        ref={uploadRef}
                        className={`${fileLists.dep.length ? styles['active'] : ''}`}
                        fileList={fileLists.dep}
                        showUploadList={{
                            showPreviewIcon: true,
                        }}
                        multiple={true}
                        onPreview={(file: UploadFile) => window.open(file.url, '_blank')}
                        customRequest={(e) => {
                            const temp: any = e;
                            const file: File = temp.file;
                            onUpload(file);
                        }}
                        disabled={chatDeactivateMessage !== undefined}
                    />
                    <Input
                        value={messageValue}
                        className={`${styles['input']}`}
                        placeholder="پیامی بنویسید..."
                        disabled={chatDeactivateMessage !== undefined}
                        prefix={
                            <>
                                {!messageValue.length && !files.dep.length && !fileLists.dep.length ? (
                                    <IoMicOutline
                                        size={20}
                                        color={theme === ThemeEnum.light ? COLORS.gray1 : COLORS.gray2}
                                        className={`pointer`}
                                        onClick={openVoiceRecorder}
                                    />
                                ) : (
                                    <>
                                        {loading ? (
                                            <Spin size="small" />
                                        ) : (
                                            <BiSend
                                                color={COLORS.primary}
                                                size={20}
                                                className={`pointer`}
                                                onClick={submitMessage}
                                            />
                                        )}
                                    </>
                                )}
                            </>
                        }
                        addonAfter={
                            <Button
                                onClick={handleButtonClick}
                                type="text"
                                icon={<CgAttachment size={20} color={COLORS.gray1} />}
                                disabled={chatDeactivateMessage !== undefined}
                            />
                        }
                        onChange={(e) => setMessageValue(e.target.value)}
                    />
                </>
            ) : (
                <div className={`${styles['voice-layout']}`}>
                    {audioBlob && <audio controls src={URL.createObjectURL(audioBlob)} className="w-100" />}
                    <div className={`center-content-y flex-between`}>
                        {recordStatus === 'stop' ? (
                            <>
                                {loading ? (
                                    <Spin size="small" />
                                ) : (
                                    <BiSend
                                        color={COLORS.primary}
                                        size={20}
                                        className={`pointer`}
                                        onClick={voiceSubmit}
                                    />
                                )}
                            </>
                        ) : (
                            <MdOutlinePauseCircle
                                color={theme === ThemeEnum.light ? COLORS.black1 : COLORS.gray2}
                                size={20}
                                className={`pointer`}
                                onClick={stopVoiceRecorder}
                            />
                        )}
                        <div className="flex-column center-content g-1">
                            <p className="t-p2 t-l-black1">{formatTime(count)}</p>
                        </div>
                        <div className="g-2 center-content-y">
                            <IoMdTrash
                                onClick={removeVoiceRecorder}
                                color={COLORS.red}
                                size={20}
                                className={`pointer`}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CustomInputComp;
